import {
  FATIGUE_CAST_FLOOR,
  SIGN_MAX_PER_CASTER,
  STORM_HEIGHT,
  STORM_TICK_SECONDS,
} from '../constants.ts';
import { type Dice, rollDice } from '../dice.ts';
import { dist, forwardVector, rightVector, spreadDirection, upVector, type Vec3 } from '../math.ts';
import {
  clearProtectionFrom,
  drainPp,
  heal,
  isEmbodied,
  playerHeight,
  type PlayerState,
} from '../player.ts';
import { tierForSlot } from '../progression.ts';
import { chosenElement, GLOBAL_COOLDOWN, type SpellDef, SPELLS_BY_ID } from '../spells.ts';
import { dealDamage } from './death.ts';
import { groundUnderAim, placeSign, placeWall } from './placedEffects.ts';
import { applyBurning, applyChill, applyShock, volleyPattern } from './projectiles.ts';
import {
  eyePos,
  PLAYER_RADIUS,
  type PlayerInput,
  type SimEvent,
  type SimState,
  type WorldQuery,
} from './state.ts';

/**
 * Casting: readying a slot, the wind-up, and what a spell does once it lands.
 *
 * The rules this module is arranged around: nothing interrupts a cast but the
 * caster; nothing is spent until a cast takes effect; and everything is
 * re-checked at the moment it lands rather than trusted from when it began. See
 * "Casting" and "Combat feel" in CURRENT.md.
 */

/** Metres per step of a `farBonus`. */
export const FAR_BONUS_STEP = 10;

export const MAX_PROJECTILE_RANGE = 160;
/**
 * Reach of a touch spell. Short on purpose — these land instantly on whoever is
 * under the crosshair with no travel to dodge and no projectile to see coming,
 * and fourteen metres of that is a hitscan rifle rather than a touch. No timer
 * would help: they do not travel, so distance is the only thing there is to cap.
 */
export const TOUCH_RANGE = 4;

/**
 * Index into a spell's per-tier tables, or −1 when the slot has not unlocked.
 *
 * The minus one is the point. Clamping a locked slot to tier I would let a
 * level-1 mage cast an Area spell that is not meant to exist for another four
 * levels, and it would do it silently.
 */
export function tierIndex(p: PlayerState, spell: SpellDef): number {
  const tier = tierForSlot(spell.slot, p.level);
  if (tier <= 0) return -1;
  return Math.min(spell.ppCost.length - 1, tier - 1);
}

/**
 * Tab retunes the readied protection. Only the protection lines offer a choice,
 * but the count is banked either way — otherwise presses made while an ordinary
 * spell is readied would queue up and fire all at once on the next Resist.
 */
export function cycleElement(p: PlayerState, input: PlayerInput): void {
  if (input.elementCycles <= p.elementCyclesSeen) return;
  const steps = input.elementCycles - p.elementCyclesSeen;
  p.elementCyclesSeen = input.elementCycles;

  const spell = SPELLS_BY_ID[p.loadout[p.selectedSlot]];
  const choices = spell?.elementChoices;
  if (!choices || choices.length < 2) return;
  p.slotElement[p.selectedSlot] =
    (p.slotElement[p.selectedSlot] + steps) % choices.length;
}

export function tryCast(
  s: SimState, p: PlayerState, world: WorldQuery, out: SimEvent[],
): void {
  // Valhalla is sealed. Nothing casts in there, which is most of what makes it
  // a penalty box rather than a scenic route home.
  if (p.inValhalla) return;

  const spellId = p.loadout[p.selectedSlot];
  const spell = SPELLS_BY_ID[spellId];
  if (!spell) return;
  if (p.casting) return;
  if (p.cooldowns[p.selectedSlot] > 0) return;
  // Universal means universal: heals, walls and protections wait their turn too.
  if (p.gcd > 0) return;
  if (p.fatigue < Math.max(FATIGUE_CAST_FLOOR, spell.fatigueCost)) return;

  const ti = tierIndex(p, spell);
  // Carried but not yet learned. Costs nothing and starts nothing, the same as
  // any other cast that could not happen.
  if (ti < 0) return;

  const cost = spell.ppCost[ti];
  if (p.pp < cost) return;

  // A spell with a wind-up starts one and resolves later. Nothing is spent yet:
  // an abandoned cast has to cost nothing, the same as one that reached nobody.
  if (spell.castTime && spell.castTime > 0) {
    p.casting = {
      spellId,
      slot: p.selectedSlot,
      remaining: spell.castTime,
      total: spell.castTime,
    };
    out.push({ type: 'cast-start', playerId: p.id, spellId, seconds: spell.castTime });
    return;
  }

  resolveCast(s, p, spell, world, out);
}

/**
 * Advances a wind-up, and lets it go when it finishes.
 *
 * Readying a different slot abandons it. That is the only way out: no interrupt
 * on damage, no pushback, no root breaking it — the cost of a cast time is that
 * you move at half speed while you pay it, and dying is the only thing anyone
 * else can do about it.
 */
export function tickCasting(
  s: SimState, p: PlayerState, world: WorldQuery, dt: number, out: SimEvent[],
): void {
  const cast = p.casting;
  if (!cast) return;

  if (!isEmbodied(p) || p.selectedSlot !== cast.slot) {
    p.casting = null;
    out.push({ type: 'cast-cancel', playerId: p.id, spellId: cast.spellId });
    return;
  }

  cast.remaining -= dt;
  if (cast.remaining > 0) return;

  p.casting = null;
  const spell = SPELLS_BY_ID[cast.spellId];
  if (spell) resolveCast(s, p, spell, world, out);
}

/**
 * Spends for a spell and puts it into the world, in that order only if it took.
 *
 * Everything is re-checked at this point rather than trusted from when the
 * wind-up began — power can have drained away over half a second, and a spell
 * that cannot be paid for at the moment it would land simply does not.
 */
export function resolveCast(
  s: SimState, p: PlayerState, spell: SpellDef, world: WorldQuery, out: SimEvent[],
): void {
  const slot = p.selectedSlot;
  const ti = tierIndex(p, spell);
  if (ti < 0) return;

  const cost = spell.ppCost[ti];
  if (p.pp < cost) return;

  const dice = spell.dice?.[ti];

  // Resolve the spell before charging for it. A cast that takes your power,
  // starts your cooldown and writes itself into the log while producing nothing
  // in the world is indistinguishable from a bug — which is exactly how the
  // stubbed sign case presented before it was finished. Nothing is spent unless
  // something happened.
  const landed: SimEvent[] = [];
  if (!applyCast(s, p, spell, ti, dice, world, landed)) {
    /*
     * Nothing is charged, but something is said.
     *
     * `applyCast` reports a reason by pushing a `fizzle` into the same array it
     * would have put the results in, so a branch that has one is a branch that
     * already had the array to hand. The rest of `landed` is dropped: a cast
     * that did not happen has no results, and anything else in there would be
     * the effects of a spell that was never paid for.
     */
    const why = landed.find((e) => e.type === 'fizzle');
    if (why) out.push(why);
    return;
  }

  p.pp -= cost;
  p.fatigue = Math.max(0, p.fatigue - spell.fatigueCost);
  p.cooldowns[slot] = spell.cooldown;
  // Only a cast that landed locks the bar, the same way only a cast that landed
  // costs power.
  p.gcd = GLOBAL_COOLDOWN;
  out.push({ type: 'cast', playerId: p.id, spellId: spell.id, tier: ti + 1 });
  out.push(...landed);
}

/** Returns false when the spell could not take effect, so nothing is charged. */
export function applyCast(
  s: SimState, p: PlayerState, spell: SpellDef, ti: number,
  dice: Dice | undefined, world: WorldQuery, out: SimEvent[],
): boolean {
  switch (spell.kind) {
    case 'projectile':
    case 'ball': {
      if (!dice) return false;
      const aim = forwardVector(p.yaw, p.pitch);
      // Balls are lobbed rather than aimed and are forgiving by nature; only the
      // darts and bolts pay for being fired on the move.
      const dir = spell.kind === 'projectile'
        ? spreadDirection(aim, p.spread, () => s.rng.next())
        : aim;

      /*
       * A volley leaves as several bodies rather than one.
       *
       * Wounding Triad said "three bolts in a tight spread" and threw a single
       * axe, which is the same shape of fault Electrocute's chain had: a claim
       * in the notes and nothing in the simulation. Three separate throws means
       * three separate rolls, three separate hits, and the possibility of
       * landing two of three — which is what a spread is *for*, and is not
       * something one projectile with a bigger die can express.
       */
      const right = rightVector(p.yaw);
      const up = upVector(p.yaw, p.pitch);
      for (const offset of volleyPattern(spell.volley ?? 1)) {
        s.projectiles.push({
          id: s.nextProjectileId++,
          ownerId: p.id,
          faction: p.faction,
          spellId: spell.id,
          element: spell.element,
          dice,
          pos: {
            x: eyePos(p).x + right.x * offset.x + up.x * offset.y,
            y: eyePos(p).y + right.y * offset.x + up.y * offset.y,
            z: eyePos(p).z + right.z * offset.x + up.z * offset.y,
          },
          dir,
          speed: spell.projectileSpeed ?? 35,
          travelled: 0,
          // A slow projectile with the usual range spends four seconds in the
          // air and arrives where nobody is. Range is what keeps it close.
          maxRange: spell.maxRange ?? MAX_PROJECTILE_RANGE,
          radius: spell.kind === 'ball' ? (spell.radius ?? 4) : 0,
          tier: ti + 1,
          burnTicks: spell.burnTicks?.[ti],
          chill: spell.chill,
          chillSeconds: spell.chillSeconds,
          chainTargets: spell.chainTargets?.[ti],
          ppDrain: spell.ppDrain?.[ti],
          knockback: spell.knockback?.[ti],
        });
      }
      break;
    }

    /*
     * A bolt: aimed down the crosshair and arriving the instant it is cast.
     *
     * These were projectiles, and slow ones, which made every secondary a
     * heavier version of the primary beside it — same aim, same lead, same
     * dodge, more damage. As a hitscan the wind-up becomes the whole cost: a
     * quarter of a second of standing still and committing to where somebody
     * *will* be, against a dart that can be thrown at where they are. Two
     * different skills rather than two settings of one.
     *
     * Unlike `touch`, the endpoint matters even on a miss. A bolt that hits a
     * wall still has to be drawn arriving somewhere, or the caster cannot tell a
     * near miss from a spell that did not fire.
     */
    case 'bolt': {
      if (!dice) return false;
      const from = eyePos(p);
      const dir = forwardVector(p.yaw, p.pitch);
      const reach = spell.range ?? 45;

      const wall = world.raycast(from, dir, reach);
      const target = pickTargetAlongRay(s, p, from, dir, wall ? wall.distance : reach);

      const stop = target
        ? eyePos(target)
        : {
          x: from.x + dir.x * (wall?.distance ?? reach),
          y: from.y + dir.y * (wall?.distance ?? reach),
          z: from.z + dir.z * (wall?.distance ?? reach),
        };

      // Drawn whatever it hit, including nothing. A bolt is instant, so the beam
      // *is* the feedback — there is no projectile to watch.
      out.push({ type: 'beam', playerId: p.id, spellId: spell.id, element: spell.element, from, to: stop });

      if (target) {
        /*
         * A bolt that is worth more the further it goes.
         *
         * Backwards from almost every weapon in every game, and deliberately:
         * what is being paid for is the *sightline*, not the shot. Standing
         * still in a long open lane for a third of a second to charge is a thing
         * Shock Bolt never asks of anybody, and without this there is no reason
         * to ask it.
         *
         * Measured to the target rather than to where the beam stopped, so a
         * body at twelve metres is worth the same wherever the wall behind it
         * happens to be. Whole ten-metre steps: a number a player can arrive at
         * by eye rather than a curve they have to feel out. The spell's own
         * range is the cap — there is nothing to add for distance a bolt cannot
         * cross.
         */
        const far = spell.farBonus
          ? Math.floor(dist(from, eyePos(target)) / FAR_BONUS_STEP) * spell.farBonus
          : 0;
        dealDamage(s, p, target, rollDice(s.rng, dice) + far, spell.element, out);
        // Everything a spell does beyond its dice, the same list a projectile
        // applies on impact — a bolt is a different delivery, not a different
        // spell.
        if (spell.burnTicks) applyBurning(target, spell.burnTicks[ti] ?? 1, p.id);
        if (spell.chill) applyChill(target, spell.chill, spell.chillSeconds ?? 1.5, p.id);
        if (spell.shockDamage) applyShock(target, spell.shockDamage[ti] ?? 1, p.id);
        if (spell.ppDrain) drainPp(target, spell.ppDrain[ti] ?? 0);
      }
      break;
    }

    case 'touch': {
      // Lands instantly on whoever is under the crosshair, at short range.
      if (!dice) return false;
      const from = eyePos(p);
      const dir = forwardVector(p.yaw, p.pitch);
      /*
       * The spell's own range, not a constant.
       *
       * Pain declares `range: 14` and its note says so, and the sim reached four
       * metres for every touch spell in the game — so the one Mentalist control
       * spell was a melee spell described as a mid-range one, and unlandable in
       * practice. `TOUCH_RANGE` stays as the fallback for a touch spell that
       * declares nothing, which is what Soul Shock does.
       */
      const reach = spell.range ?? TOUCH_RANGE;
      const target = pickTargetAlongRay(s, p, from, dir, reach);
      // A touch that reached nobody charged full price and started the full
      // cooldown, which reads from the caster's side as the spell being broken
      // rather than as a miss. Fourth spell kind to have had this.
      if (!target || world.segmentBlocked(from, eyePos(target))) {
        // Still free, but no longer silent — see the `fizzle` event.
        out.push({
          type: 'fizzle', playerId: p.id, spellId: spell.id,
          reason: 'No enemy in range',
        });
        return false;
      }
      dealDamage(s, p, target, rollDice(s.rng, dice), spell.element, out);
      if (spell.shockDamage) applyShock(target, spell.shockDamage[ti] ?? 1, p.id);
      break;
    }

    case 'heal': {
      if (!dice) return false;
      const mended = heal(p, rollDice(s.rng, dice));
      out.push({ type: 'heal', targetId: p.id, sourceId: p.id, amount: mended });
      break;
    }

    case 'heal-other': {
      if (!dice) return false;
      const from = eyePos(p);
      const dir = forwardVector(p.yaw, p.pitch);
      const target = pickTargetAlongRay(s, p, from, dir, 40, true);
      // Nobody in the way. Costs nothing, like any other spell that cannot land.
      // An ally rather than an enemy, so it says a different thing: a Cleric
      // aiming Heal Other at an enemy is making a mistake worth naming.
      if (!target) {
        out.push({
          type: 'fizzle', playerId: p.id, spellId: spell.id,
          reason: 'No ally in range',
        });
        return false;
      }
      const mended = heal(target, rollDice(s.rng, dice));
      out.push({ type: 'heal', targetId: target.id, sourceId: p.id, amount: mended });
      break;
    }

    /*
     * A guard cast on somebody else.
     *
     * The only protection in the game that is not self-cast, which is what makes
     * it a utility rather than a defence: it is something a Mentalist does *for*
     * the team, and choosing the school is a read of what the enemy has been
     * throwing rather than a habit.
     *
     * One protection per caster still holds, and the caster is the Mentalist —
     * so guarding a second teammate drops the first. A Mentalist covers one
     * mage at a time and has to choose which.
     */
    case 'buff-other': {
      const from = eyePos(p);
      const dir = forwardVector(p.yaw, p.pitch);
      const friend = pickTargetAlongRay(s, p, from, dir, spell.range ?? 26, true);
      if (!friend || world.segmentBlocked(from, eyePos(friend))) {
        out.push({
          type: 'fizzle', playerId: p.id, spellId: spell.id,
          reason: 'No ally in range',
        });
        return false;
      }

      const remaining = spell.duration ?? 18;
      /*
       * Cleared from *everyone*, not just from whoever is about to receive it.
       *
       * One protection per caster is the rule, and for a self-cast guard the
       * caster and the bearer are the same person so clearing the target is the
       * same thing. Cast on somebody else they come apart: guarding a second
       * teammate has to take the guard off the first, or a Mentalist accumulates
       * the whole team behind resistances over a match.
       */
      for (const anybody of s.players) clearProtectionFrom(anybody, p.id);
      friend.effects.push({
        spellId: spell.id,
        element: chosenElement(spell, p.slotElement[p.selectedSlot]),
        resist: 0.15 + ti * 0.08,
        remaining,
        total: remaining,
        protection: true,
        sourceId: p.id,
      });
      break;
    }

    case 'buff': {
      const base = 0.15 + ti * 0.08;
      const isPrayer = spell.id === 'prayer';
      // Prayer covers every school at a lower rate; Armor is half again a
      // Resist against the one school it is tuned to.
      const resist = isPrayer ? base * 0.6 : base * (spell.id === 'ment_armor' ? 1.5 : 1);
      const element = isPrayer ? undefined : chosenElement(spell, p.slotElement[p.selectedSlot]);
      const remaining = spell.duration ?? 20;

      // One protection per caster. Retuning and recasting replaces what you had
      // rather than adding to it, so covering a second school means asking a
      // teammate rather than cycling Tab.
      clearProtectionFrom(p, p.id);
      p.effects.push({
        spellId: spell.id, element, resist, remaining, total: remaining,
        protection: true, sourceId: p.id,
      });
      break;
    }

    case 'debuff': {
      // Thrown, not pointed. These were hitscan against a fixed 1.1m cylinder,
      // which is three degrees of latitude at sixteen metres and one and a half
      // at thirty — and a miss drew nothing, made no sound and cost nothing, so
      // from the caster's side pressing the key simply did nothing at all. Every
      // one of them has carried a projectile speed since M1; now it is used.
      s.projectiles.push({
        id: s.nextProjectileId++,
        ownerId: p.id,
        faction: p.faction,
        spellId: spell.id,
        element: spell.element,
        dice: dice ?? { count: 0, sides: 0 },
        pos: eyePos(p),
        dir: forwardVector(p.yaw, p.pitch),
        speed: spell.projectileSpeed ?? 34,
        travelled: 0,
        maxRange: MAX_PROJECTILE_RANGE,
        radius: 0,
        applies: 'debuff',
        tier: ti + 1,
        duration: spell.durations?.[ti] ?? spell.duration ?? 6,
        ticks: spell.ticks,
      });
      break;
    }

    case 'arc': {
      if (!dice) return false;
      const struck = nearestVisibleEnemy(s, p, world, spell.range ?? 14);
      // Nothing in reach. Costs nothing, same as any other spell that cannot
      // land — and says so, because an arc hits automatically when it hits at
      // all, so silence is the only thing distinguishing "nobody there" from
      // "this key is broken".
      if (!struck) {
        out.push({
          type: 'fizzle', playerId: p.id, spellId: spell.id,
          reason: 'No enemy in range',
        });
        return false;
      }
      dealDamage(s, p, struck, rollDice(s.rng, dice), spell.element, out);
      out.push({ type: 'arc', sourceId: p.id, targetId: struck.id, element: spell.element });
      break;
    }

    case 'storm': {
      if (!dice) return false;
      const ground = groundUnderAim(world, eyePos(p), forwardVector(p.yaw, p.pitch));
      // Aimed at open sky with no floor beneath it. Cannot happen in an
      // enclosed arena, but it costs nothing when it does.
      if (!ground) return false;
      s.storms.push({
        id: s.nextPlacedId++,
        ownerId: p.id,
        faction: p.faction,
        spellId: spell.id,
        element: spell.element,
        dice,
        pos: ground,
        radius: spell.radius ?? 6,
        height: STORM_HEIGHT,
        remaining: spell.duration ?? 4,
        nextTick: STORM_TICK_SECONDS,
        chill: spell.chill,
        chillSeconds: spell.chillSeconds,
      });
      break;
    }

    case 'wall': {
      const barrier = placeWall(s, p, spell, ti, world, dice);
      if (!barrier) return false;
      s.barriers.push(barrier);
      break;
    }

    case 'sign': {
      const sign = placeSign(s, p, spell, world, dice);
      if (!sign) return false;

      // Three live per Arcanist, oldest first out. A 45-second life on an
      // eight-second cooldown otherwise lets one player seal a doorway for free.
      const mine = s.signs.filter((x) => x.ownerId === p.id);
      if (mine.length >= SIGN_MAX_PER_CASTER) {
        const oldest = mine[0];
        const at = s.signs.indexOf(oldest);
        if (at >= 0) s.signs.splice(at, 1);
      }
      s.signs.push(sign);
      break;
    }
  }

  return true;
}

/**
 * Closest enemy in range that the caster can actually see.
 *
 * Line of sight is still required — an arc will not reach through a wall just
 * because it does not need aiming. That is the only thing keeping Jolts honest
 * in a building full of corners.
 */
export function nearestVisibleEnemy(
  s: SimState, caster: PlayerState, world: WorldQuery, range: number,
): PlayerState | null {
  const from = eyePos(caster);
  let best: PlayerState | null = null;
  let bestDist = Infinity;

  for (const other of s.players) {
    if (other.id === caster.id) continue;
    if (!isEmbodied(other)) continue;
    if (other.faction === caster.faction) continue;

    const head = { x: other.pos.x, y: other.pos.y + playerHeight(other) * 0.75, z: other.pos.z };
    const d = dist(from, head);
    if (d > range || d >= bestDist) continue;
    if (world.segmentBlocked(from, head)) continue;

    best = other;
    bestDist = d;
  }
  return best;
}

export function pickTargetAlongRay(
  s: SimState, caster: PlayerState, from: Vec3, dir: Vec3, range: number,
  wantAlly = false,
): PlayerState | undefined {
  let best: PlayerState | undefined;
  let bestT = Infinity;
  for (const other of s.players) {
    if (other.id === caster.id) continue;
    if (!isEmbodied(other)) continue;
    const ally = other.faction === caster.faction;
    if (wantAlly !== ally) continue;
    const c = {
      x: other.pos.x - from.x,
      y: other.pos.y + playerHeight(other) / 2 - from.y,
      z: other.pos.z - from.z,
    };
    const t = c.x * dir.x + c.y * dir.y + c.z * dir.z;
    if (t < 0 || t > range) continue;
    const closest = { x: from.x + dir.x * t, y: from.y + dir.y * t, z: from.z + dir.z * t };
    const perp = dist(closest, {
      x: other.pos.x, y: other.pos.y + playerHeight(other) / 2, z: other.pos.z,
    });
    if (perp <= PLAYER_RADIUS * 2 && t < bestT) {
      bestT = t;
      best = other;
    }
  }
  return best;
}
